import { useState, useEffect } from 'react';
import popupAlert from '../../utilities/popup-alert';
import {
  checkItemInSaveForLater,
  addItemToSaveForLater,
  removeItemFromSaveForLater
} from './SaveForLaterApi';
import LoaderMask from '../../components/LoaderMask';
import Loader from '../../components/Loader';
import Icon from '../../components/Icon';
import { SaveForLaterResponse } from '../../types/generated';
import { updateSavedItemsCount } from '../../helpers/saveforlater';

interface AddToSaveForLaterProps {
  catalogueRef?: string | null;
  modifier?: string | null;
  buttonTitle?: string;
  size: '' | 'sm' | 'lg';
  quantity?: number | '';
}

export default function AddToSaveForLater({
  catalogueRef,
  quantity,
  buttonTitle,
  modifier
}: AddToSaveForLaterProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSavedForLater, setIsSavedForLater] = useState<boolean>(false);

  useEffect(() => {
    if (catalogueRef) {
      setIsLoading(true);
      checkItemInSaveForLater(catalogueRef).then(
        (data: SaveForLaterResponse) => {
          if (data?.isSuccess) {
            setIsLoading(false);
            setIsSavedForLater(true);
          } else {
            setIsLoading(false);
            setIsSavedForLater(false);
          }
        }
      );
    }
  }, [catalogueRef]);

  function handleAddItemToSaveForLater() {
    let newQuantity = 1;

    if (quantity) {
      if (isNaN(quantity) || quantity < 1) {
        popupAlert.showNegative(
          'Please check the quantity field, only positive numbers are allowed.'
        );
        return;
      } else {
        newQuantity = quantity;
      }
    }
    addItemToSaveForLater(catalogueRef || null, modifier || null, newQuantity)
      .then((data: SaveForLaterResponse) => {
        if (data?.isSuccess) {
          updateSavedItemsCount(data.itemCount);
          setIsSavedForLater(true);
          popupAlert.show(
            'Item added to save for later',
            popupAlert.types.positive,
            popupAlert.icons.positive,
            5000,
            'View saved items',
            '/saved-for-later'
          );
        } else {
          popupAlert.showNegative('Failed to add item to save for later');
        }
      })
      .catch((error: Error) => {
        console.log('err', error);
        popupAlert.showNegative('Failed to add item to save for later');
      });
  }

  function handleRemoveItemFromSaveForLater() {
    removeItemFromSaveForLater(catalogueRef || null)
      .then((data: SaveForLaterResponse) => {
        if (data?.isSuccess) {
          updateSavedItemsCount(data.itemCount);
          setIsSavedForLater(false);
          popupAlert.showPositive('Item removed from save for later');
        } else {
          popupAlert.showNegative('Failed to remove item from save for later');
        }
      })
      .catch((error: Error) => {
        console.log('err', error);
        popupAlert.showNegative('Failed to remove item from save for later');
      });
  }

  return (
    <div className="save-for-later">
      {isLoading ? (
        <LoaderMask type="relative">
          <Loader size="xsmall" />
        </LoaderMask>
      ) : (
        <>
          <div className="d-block d-lg-none">
            {isSavedForLater ? (
              <button
                className="btn btn-outline-primary btn-block"
                onClick={handleRemoveItemFromSaveForLater}
              >
                <span className="d-inline-block pr-2">
                  <Icon iconName="heart" modifierCss="icon--heart" />{' '}
                </span>
                {buttonTitle || 'Saved for Later'}
              </button>
            ) : (
              <button
                className="btn btn-outline-primary btn-block"
                onClick={handleAddItemToSaveForLater}
              >
                <span className="d-inline-block pr-2">
                  <Icon iconName="heart-o" modifierCss="icon--heart-o" />{' '}
                </span>
                {buttonTitle || 'Save for Later'}
              </button>
            )}
          </div>

          <div className="d-none d-lg-block">
            {isSavedForLater ? (
              <button
                className="btn btn-link"
                onClick={handleRemoveItemFromSaveForLater}
              >
                <span className="d-inline-block pr-2">
                  <Icon iconName="heart" modifierCss="icon--heart" />{' '}
                </span>
                {buttonTitle || 'Saved for Later'}
              </button>
            ) : (
              <button
                className="btn btn-link"
                onClick={handleAddItemToSaveForLater}
              >
                <span className="d-inline-block pr-2">
                  <Icon iconName="heart-o" modifierCss="icon--heart-o" />{' '}
                </span>
                {buttonTitle || 'Save for Later'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
